import '../styles/gallery.css';
import { projects } from '../content/projects';
import type { Project } from '../content/projects';
import type { Media } from '../content/media';

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

function esc(value: string): string {
  return value.replace(/[&<>"]/g, c => ESCAPES[c] ?? c);
}

function getSlug(): string | undefined {
  return /^#\/gallery\/([^/]+)$/.exec(location.hash)?.[1];
}

function isVideo(media: Media): boolean {
  return media.kind === 'local-video' || media.kind === 'youtube' || media.kind === 'vimeo';
}

function placeholderHtml(glyph: string): string {
  return `<div class="media-placeholder" aria-hidden="true">${glyph}</div>`;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'external link';
  }
}

/** Rail thumbs carry `alt=""` — the link is named by its `.rail-label`, not the image. */
function railThumbHtml(media: Media, slug: string): string {
  const local = (filename: string): string =>
    `<img src="/projects/${encodeURIComponent(slug)}/${encodeURIComponent(filename)}" alt="" loading="lazy" decoding="async">`;

  switch (media.kind) {
    case 'local-image':
      return local(media.filename);
    case 'local-video':
      return media.poster ? local(media.poster) : placeholderHtml('▶');
    case 'youtube':
      return `<img src="https://img.youtube.com/vi/${encodeURIComponent(media.id)}/hqdefault.jpg" alt="" loading="lazy" decoding="async">`;
    case 'vimeo':
      return placeholderHtml('▶');
    case 'external-link':
      return media.thumbnail ? local(media.thumbnail) : placeholderHtml('↗');
    case 'local-model':
      return media.poster ? local(media.poster) : placeholderHtml('⬢');
  }
}

/**
 * `autoplay` is true for at most one item per project — the first video. Muted
 * throughout: unmuted autoplay is blocked by every browser, and local clips are
 * silent anyway.
 */
function mediaHtml(media: Media, slug: string, autoplay: boolean): string {
  const path = (filename: string): string =>
    `/projects/${encodeURIComponent(slug)}/${encodeURIComponent(filename)}`;

  const embedFrame = (src: string, title: string): string =>
    `<iframe src="${src}" title="${esc(title)}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;

  switch (media.kind) {
    case 'local-image':
      return `<figure class="media-item">
        <img src="${path(media.filename)}" alt="${esc(media.alt)}" loading="lazy" decoding="async"${media.width ? ` width="${media.width}"` : ''}${media.height ? ` height="${media.height}"` : ''}>
      </figure>`;
    case 'local-video':
      return `<figure class="media-item">
        <video controls playsinline muted preload="${autoplay ? 'auto' : 'metadata'}"${autoplay ? ' autoplay loop' : ''}${media.poster ? ` poster="${path(media.poster)}"` : ''}>
          <source src="${path(media.filename)}">
        </video>
      </figure>`;
    case 'youtube':
      return `<figure class="media-item media-embed" style="aspect-ratio:${media.aspectRatio}">
        ${
          autoplay
            ? embedFrame(
                `https://www.youtube-nocookie.com/embed/${encodeURIComponent(media.id)}?autoplay=1&amp;mute=1&amp;playsinline=1`,
                media.title,
              )
            : `<button class="embed-trigger" data-src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(media.id)}?autoplay=1" aria-label="Play ${esc(media.title)}">
          <img src="https://img.youtube.com/vi/${encodeURIComponent(media.id)}/hqdefault.jpg" alt="" loading="lazy" decoding="async">
          <span class="play-overlay" aria-hidden="true">▶</span>
        </button>`
        }
      </figure>`;
    case 'vimeo':
      return `<figure class="media-item media-embed" style="aspect-ratio:${media.aspectRatio}">
        ${
          autoplay
            ? embedFrame(
                `https://player.vimeo.com/video/${encodeURIComponent(media.id)}?autoplay=1&amp;muted=1&amp;playsinline=1`,
                media.title,
              )
            : `<button class="embed-trigger" data-src="https://player.vimeo.com/video/${encodeURIComponent(media.id)}?autoplay=1" aria-label="Play ${esc(media.title)}">
          <div class="embed-placeholder">
            <span class="play-overlay" aria-hidden="true">▶</span>
            <span>${esc(media.title)}</span>
          </div>
        </button>`
        }
      </figure>`;
    case 'external-link':
      return `<div class="media-item media-external">
        <a href="${esc(media.url)}" target="_blank" rel="noopener noreferrer" class="external-link-card">
          <span class="external-thumb">${media.thumbnail ? `<img src="${path(media.thumbnail)}" alt="" loading="lazy" decoding="async">` : placeholderHtml('↗')}</span>
          <span class="external-body">
            <span class="external-title">${esc(media.title)}</span>
            <span class="external-host">${esc(hostOf(media.url))} <span aria-hidden="true">↗</span></span>
          </span>
        </a>
      </div>`;
    case 'local-model':
      return `<figure class="media-item media-model" style="aspect-ratio:${media.aspectRatio ?? '16/9'}">
        ${media.poster ? `<img src="${path(media.poster)}" alt="" loading="lazy" decoding="async">` : placeholderHtml('⬢')}
        <figcaption>${esc(media.title)}</figcaption>
      </figure>`;
  }
}

function renderShell(container: HTMLElement): void {
  const items = projects
    .map(p => {
      const primary = p.media[0];
      const thumb = p.thumbnail
        ? `<img src="/projects/${encodeURIComponent(p.slug)}/${encodeURIComponent(p.thumbnail)}" alt="" loading="lazy" decoding="async">`
        : primary
          ? railThumbHtml(primary, p.slug)
          : placeholderHtml('□');
      return `<li class="rail-item">
        <a href="#/gallery/${encodeURIComponent(p.slug)}" class="rail-link" data-slug="${esc(p.slug)}">
          <span class="rail-thumb">${thumb}</span>
          <span class="rail-label">${esc(p.title)}</span>
        </a>
      </li>`;
    })
    .join('');

  container.innerHTML = `
    <section class="page page-gallery">
      <div class="gallery-split content-panel">
        <nav class="gallery-rail" aria-label="Projects">
          <h2 class="visually-hidden">Work</h2>
          <ul class="rail-list" role="list">${items}</ul>
        </nav>
        <div class="gallery-detail"></div>
      </div>
    </section>
  `;
}

function detailHtml(project: Project): string {
  // -1 when the project has no video, which no index can equal.
  const autoplayIndex = project.media.findIndex(isVideo);
  const media = project.media
    .map((m, i) => mediaHtml(m, project.slug, i === autoplayIndex))
    .join('');
  const links =
    project.links
      ?.map(
        l =>
          `<a href="${esc(l.url)}" target="_blank" rel="noopener noreferrer" class="project-link project-link--${l.kind}" data-kind="${l.kind}">${esc(l.label ?? l.kind)}<span class="visually-hidden"> (opens in a new tab)</span></a>`,
      )
      .join('') ?? '';

  return `<article class="project-detail">
    <header class="project-header">
      <h2 tabindex="-1">${esc(project.title)}</h2>
      <p class="project-meta">${esc(project.client)} · ${project.year}</p>
    </header>
    <div class="project-description">${project.description.map(p => `<p>${p}</p>`).join('')}</div>
    ${links ? `<div class="project-links">${links}</div>` : ''}
    ${media ? `<div class="project-media">${media}</div>` : ''}
  </article>`;
}

function installImageFallbacks(root: ParentNode): void {
  root.querySelectorAll('img').forEach(img => {
    img.addEventListener(
      'error',
      () => {
        const holder = img.parentElement;
        img.remove();
        if (holder && !holder.querySelector('.media-placeholder')) {
          const fallback = document.createElement('div');
          fallback.className = 'media-placeholder';
          fallback.setAttribute('aria-hidden', 'true');
          fallback.textContent = '□';
          holder.prepend(fallback);
        }
      },
      { once: true },
    );
  });
}

/**
 * Detaching a media element via innerHTML does not reliably halt playback —
 * an orphaned iframe can keep streaming audio. Stop everything explicitly
 * before the pane is replaced.
 */
function teardownMedia(root: ParentNode): void {
  root.querySelectorAll('video').forEach(v => v.pause());
  root.querySelectorAll('iframe').forEach(f => {
    f.src = 'about:blank';
  });
}

function installEmbedTriggers(root: ParentNode): void {
  root.querySelectorAll<HTMLButtonElement>('.embed-trigger').forEach(btn => {
    btn.addEventListener('click', () => {
      const src = btn.dataset['src'];
      if (!src) return;
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      btn.replaceWith(iframe);
    });
  });
}

function syncSelection(rail: ParentNode, slug: string | undefined): void {
  rail.querySelectorAll<HTMLAnchorElement>('.rail-link').forEach(link => {
    if (link.dataset['slug'] === slug) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

export function mount(container: HTMLElement): () => void {
  renderShell(container);
  installImageFallbacks(container);

  const pane = container.querySelector<HTMLElement>('.gallery-detail');

  const update = (moveFocus: boolean): void => {
    if (!pane) return;
    const slug = getSlug();
    const project = slug ? projects.find(p => p.slug === slug) : undefined;

    teardownMedia(pane);
    pane.innerHTML = project ? detailHtml(project) : '';
    pane.scrollTop = 0;
    installImageFallbacks(pane);
    installEmbedTriggers(pane);
    syncSelection(container, project?.slug);

    if (moveFocus) pane.querySelector<HTMLElement>('.project-header h2')?.focus();
  };

  update(false);

  const onHashChange = (): void => {
    if (location.hash.startsWith('#/gallery')) update(true);
  };
  window.addEventListener('hashchange', onHashChange);

  return () => {
    window.removeEventListener('hashchange', onHashChange);
    teardownMedia(container);
    container.innerHTML = '';
  };
}
